import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import clsx from 'clsx';
import { history } from '../../../Routes/history';
import { ROUTE_NAMES } from '../../../Routes/Routes';
import { Company } from '../../../Configs/general';

const CONDITION_CODE = {
  notLoggedIn: 'NOT_LOGGED_IN',
  digitalInvoiceNotActivatedInCampaign:
    'DIGITAL_INVOICE_NOT_ACTIVATED_IN_CAMPAIGN',
  invoiceNotPaid: 'INVOICE_NOT_PAID'
};

const getActionLabel = (code, contractId) => {
  switch (code) {
    case CONDITION_CODE.notLoggedIn:
      return 'Iniciar sesión';
    case CONDITION_CODE.digitalInvoiceNotActivatedInCampaign:
      return contractId
        ? 'Activar factura digital'
        : `Asociar ${Company.contractConjugation.regular.singular.main}`;
    case CONDITION_CODE.invoiceNotPaid:
      return 'Pagar factura';
    default:
      return null;
  }
};

const handleAction = (code, contractId) => {
  switch (code) {
    case CONDITION_CODE.notLoggedIn:
      history.push('/login');
      break;
    case CONDITION_CODE.digitalInvoiceNotActivatedInCampaign:
      if (contractId) {
        history.push(`/contracts/${contractId}&fd`);
      } else {
        console.log('Hola');
        history.push(ROUTE_NAMES.associate);
      }
      break;
    case CONDITION_CODE.invoiceNotPaid:
      history.push(ROUTE_NAMES.payments, {
        type: 'contract',
        value: contractId
      });
      break;
    default:
      break;
  }
};

const shouldShowAction = (code, contractId, currentUser, digitalInvoice) => {
  if (!currentUser) {
    return code === CONDITION_CODE.notLoggedIn;
  }

  if (!contractId) {
    return code === CONDITION_CODE.digitalInvoiceNotActivatedInCampaign;
  }

  if (
    code === CONDITION_CODE.digitalInvoiceNotActivatedInCampaign &&
    digitalInvoice
  ) {
    return false;
  }

  return true;
};

const CampaignConditionsList = ({
  conditions,
  contractId,
  currentUser,
  digitalInvoice
}) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.list}>
        {conditions.map((condition, index) => {
          const { code, message, subtitle, met } = condition;
          const actionLabel = met ? null : getActionLabel(code, contractId);
          const isLast = index === conditions.length - 1;

          return (
            <div
              key={code}
              className={clsx(classes.item, { [classes.itemBorder]: !isLast })}
            >
              <div className={classes.itemContent}>
                <div
                  className={clsx(classes.iconWrapper, {
                    [classes.iconMet]: met,
                    [classes.iconBreached]: !met
                  })}
                >
                  {met ? (
                    <CheckCircleOutlineIcon fontSize="small" />
                  ) : (
                    <ErrorOutlineIcon fontSize="small" />
                  )}
                </div>
                <div className={classes.itemBody}>
                  <Typography variant="body2" className={classes.message}>
                    {message}
                  </Typography>
                  {subtitle && (
                    <Typography variant="caption" className={classes.subtitle}>
                      {subtitle}
                    </Typography>
                  )}
                  {actionLabel &&
                    shouldShowAction(
                      code,
                      contractId,
                      currentUser,
                      digitalInvoice
                    ) && (
                      <Button
                        size="small"
                        color="primary"
                        className={classes.actionButton}
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => handleAction(code, contractId)}
                      >
                        {actionLabel}
                      </Button>
                    )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    textAlign: 'left',
    padding: theme.spacing(1, 0),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5, 0)
    }
  },
  list: {
    display: 'flex',
    flexDirection: 'column'
  },
  item: {
    padding: theme.spacing(1.5, 0),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 0)
    }
  },
  itemBorder: {
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  itemContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5)
  },
  iconWrapper: {
    marginTop: theme.spacing(0.25),
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(3),
    height: theme.spacing(3)
  },
  iconMet: {
    color: theme.palette.success
      ? theme.palette.success.main
      : theme.palette.primary.main
  },
  iconBreached: {
    color: theme.palette.error.main
  },
  itemBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    flex: 1,
    minWidth: 0
  },
  message: {
    color: theme.palette.text.primary,
    lineHeight: 1.5,
    wordBreak: 'break-word'
  },
  subtitle: {
    color: theme.palette.text.secondary,
    lineHeight: 1.4
  },
  actionButton: {
    alignSelf: 'flex-start',
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightMedium,
    padding: theme.spacing(0.5, 1.5)
  }
}));

export default CampaignConditionsList;
